import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { useMember } from '@/lib/use-session';
import { resourcesKeys, type ResourceDetailView } from '@/lib/queries/resources';
import { getApiClient } from '@/lib/api-client';
import { isNotFoundApiError, throwApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { EditResourceForm } from './-components/EditResourceForm';

async function fetchResourceForEdit(
  id: string,
  isAdmin: boolean,
): Promise<ResourceDetailView> {
  const client = getApiClient();
  const path = isAdmin ? '/api/v1/admin/resources/{id}' : '/api/v1/resources/{id}';
  const { data, error, response } = await client.GET(path as '/api/v1/resources/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data as ResourceDetailView;
}

async function fetchTopics() {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/topics', {});
  return (data ?? []).map((t) => ({ id: t.id, slug: t.slug, label: t.label }));
}

export const Route = createFileRoute('/_authed/resources/$id/edit/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: EditResourcePage,
});

function EditResourcePage() {
  const { id } = Route.useParams();
  const me = useMember();
  const isAdmin = me?.platformRole === 'admin';

  const resourceQuery = useQuery({
    queryKey: resourcesKeys.detail(id),
    queryFn: () => fetchResourceForEdit(id, isAdmin),
    enabled: me !== null,
    retry: false,
  });
  const topicsQuery = useQuery({
    queryKey: ['topics'] as const,
    queryFn: fetchTopics,
  });

  if (me === null || resourceQuery.isLoading || topicsQuery.isLoading) return null;
  if (isNotFoundApiError(resourceQuery.error)) return <NotFoundPage />;
  if (resourceQuery.isError || !resourceQuery.data) throw notFound();

  const resource = resourceQuery.data;
  if (!isAdmin) return <NotFoundPage />;

  const availableTopics = topicsQuery.data ?? [];
  const slugToId = new Map(availableTopics.map((t) => [t.slug, t.id]));
  const currentTopicIds = resource.tags
    .map((t) => slugToId.get(t.slug))
    .filter((id): id is string => id !== undefined);

  return (
    <EditResourceForm
      resourceId={resource.id}
      initial={{
        title: resource.title,
        lede: resource.lede,
        bodyMarkdown: resource.bodyMarkdown,
        coverImageUrl: resource.coverImageUrl,
        topicIds: currentTopicIds,
        attachments: resource.attachments,
        scope: resource.scope,
        showAuthor: resource.showAuthor,
        kind: resource.kind,
        contentLayer: resource.contentLayer,
      }}
      availableTopics={availableTopics}
    />
  );
}
